/** Overpass-tag-filtre per kategori vi henter. */
const OVERPASS_FILTERS = [
    { kind: 'sykehus', ql: 'nwr["amenity"="hospital"]', protectedSite: true },
    { kind: 'skole', ql: 'nwr["amenity"="school"]', protectedSite: true },
    { kind: 'kirke', ql: 'nwr["amenity"="place_of_worship"]', protectedSite: true },
    { kind: 'bru', ql: 'nwr["man_made"="bridge"]', protectedSite: false },
    { kind: 'drivstoff', ql: 'nwr["amenity"="fuel"]', protectedSite: false },
    { kind: 'kraft', ql: 'nwr["power"="substation"]', protectedSite: false },
    { kind: 'militaer', ql: 'nwr["landuse"="military"]', protectedSite: false },
];
/**
 * Bygg en Overpass QL-spørring for et bbox. Standard = alle kategoriene over.
 * `out center;` gir sentroid for veier/relasjoner slik at alt får en posisjon.
 */
export function overpassQuery(bbox, kinds) {
    const b = `(${bbox.south},${bbox.west},${bbox.north},${bbox.east})`;
    const filters = OVERPASS_FILTERS.filter((f) => !kinds || kinds.includes(f.kind));
    const body = filters.map((f) => `  ${f.ql}${b};`).join('\n');
    return `[out:json][timeout:25];\n(\n${body}\n);\nout center tags;`;
}
const KIND_BY_TAG = [
    { kind: 'mast', protectedSite: false, match: (t) => t.man_made === 'mast' },
    { kind: 'tower', protectedSite: false, match: (t) => t.man_made === 'tower' },
    { kind: 'sykehus', protectedSite: true, match: (t) => t.amenity === 'hospital' },
    { kind: 'skole', protectedSite: true, match: (t) => t.amenity === 'school' || t.amenity === 'kindergarten' },
    { kind: 'kirke', protectedSite: true, match: (t) => t.amenity === 'place_of_worship' },
    { kind: 'bru', protectedSite: false, match: (t) => t.man_made === 'bridge' || t.bridge === 'yes' },
    { kind: 'drivstoff', protectedSite: false, match: (t) => t.amenity === 'fuel' },
    { kind: 'kraft', protectedSite: false, match: (t) => t.power === 'substation' || t.power === 'plant' },
    { kind: 'militaer', protectedSite: false, match: (t) => t.landuse === 'military' || t.military !== undefined },
];
function classify(tags) {
    for (const c of KIND_BY_TAG)
        if (c.match(tags))
            return { kind: c.kind, protectedSite: c.protectedSite };
    return { kind: 'annet', protectedSite: false };
}
/** Parse et Overpass JSON-svar til elementer (dropper uten id/type). */
export function parseOverpass(raw) {
    const o = (raw ?? {});
    const els = Array.isArray(o.elements) ? o.elements : [];
    const out = [];
    for (const e of els) {
        if (e && typeof e === 'object' && typeof e.id === 'number' && typeof e.type === 'string') {
            out.push(e);
        }
    }
    return out;
}
/** Map Overpass-elementer til plott-bare punkter (posisjon fra node ell. center). */
export function osmToPoints(elements) {
    const out = [];
    for (const e of elements) {
        const lat = e.lat ?? e.center?.lat;
        const lon = e.lon ?? e.center?.lon;
        if (lat === undefined || lon === undefined)
            continue;
        const tags = e.tags ?? {};
        const { kind, protectedSite } = classify(tags);
        const heightM = osmHeightM(tags);
        const elevationM = osmMeters(tags.ele);
        const p = { id: `osm:${e.type}/${e.id}`, label: tags.name ?? kind, lat, lon, kind, protected: protectedSite };
        if (heightM !== undefined)
            p.heightM = heightM;
        if (elevationM !== undefined)
            p.elevationM = elevationM;
        out.push(p);
    }
    return out;
}
/** Snarvei: bare de beskyttede sivile objektene (til CDE ProtectedSite[]). */
export function osmProtectedSites(elements) {
    const map = {
        sykehus: 'sykehus', skole: 'skole', kirke: 'kulturminne', bru: 'annet', drivstoff: 'annet',
        kraft: 'annet', militaer: 'annet', mast: 'annet', tower: 'annet', annet: 'annet',
    };
    return osmToPoints(elements)
        .filter((p) => p.protected)
        .map((p) => ({ name: p.label, lat: p.lat, lon: p.lon, kind: map[p.kind] }));
}
// ── Infrastruktur med HØYDE (inngangsdata for dekning/siktelinjer) ──────────
// Master og tårn er både KRITISK INFRASTRUKTUR (J-5) og inngangsdata til
// radio-/radardekning: radiohorisonten går som √h, så mast-/antennehøyde er
// avgjørende. Derfor bærer `InfraSite` høyde over bakken (`heightM`) OG
// terrenghøyde (`elevationM`) separat — dekningsmodulen trenger summen
// (antennens høyde over havet) og skal ikke gjette.
/** Meter fra en OSM-tagverdi. Tåler «31», «31.5», «2.5 m», «120m».
 *  Fot («120'», «400 ft») konverteres. Returnerer undefined for søppel. */
export function osmMeters(raw) {
    if (raw === undefined)
        return undefined;
    const s = raw.trim().toLowerCase().replace(',', '.');
    // Fot: «400 ft», «400feet», «120'» (uten tomme-del — vi trenger ikke presisjon).
    const feet = /^(-?\d+(?:\.\d+)?)\s*(ft|feet|')$/.exec(s);
    if (feet)
        return sane(Number(feet[1]) * 0.3048);
    const m = /^(-?\d+(?:\.\d+)?)\s*(m|meter|metres|meters)?$/.exec(s);
    return m ? sane(Number(m[1])) : undefined;
}
/** Sanity-port: negative eller absurde verdier er taggefeil, ikke data. */
function sane(n) {
    if (!Number.isFinite(n) || n < 0 || n > 3000)
        return undefined;
    return round1(n);
}
function round1(n) { return Math.round(n * 10) / 10; }
/** Konstruksjonshøyde over bakken. `height` først, så mast-/bygg-spesifikke tagger. */
export function osmHeightM(tags) {
    return osmMeters(tags.height)
        ?? osmMeters(tags['tower:height'])
        ?? osmMeters(tags['mast:height'])
        ?? osmMeters(tags['building:height']);
}
/** Overpass-filtre for infrastruktur MED høyde-relevans. */
const INFRA_FILTERS = [
    { ql: 'nwr["man_made"="mast"]' },
    { ql: 'nwr["man_made"="tower"]' },
    { ql: 'nwr["power"="plant"]' },
    { ql: 'nwr["power"="substation"]' },
    { ql: 'nwr["amenity"="hospital"]' },
];
/**
 * Overpass QL for infrastruktur-elementer i et bbox (master/tårn, kraftverk og
 * trafo, sykehus). `out center tags;` gir posisjon også for veier/relasjoner.
 * `timeoutS` går inn i Overpass' EGEN timeout — klienten må i tillegg ha sin.
 */
export function overpassInfraQuery(bbox, timeoutS = 25) {
    const b = `(${bbox.south},${bbox.west},${bbox.north},${bbox.east})`;
    const body = INFRA_FILTERS.map((f) => `  ${f.ql}${b};`).join('\n');
    return `[out:json][timeout:${Math.max(5, Math.round(timeoutS))}];\n(\n${body}\n);\nout center tags;`;
}
/** Antennens høyde over havet: terreng + konstruksjon. Undefined om ukjent. */
export function infraAntennaAmslM(s) {
    if (s.elevationM === undefined && s.heightM === undefined)
        return undefined;
    return round1((s.elevationM ?? 0) + (s.heightM ?? 0));
}
/** Kommunikasjonsrelevante `tower:type`-verdier (radiohorisont-kandidater). */
const KOMM_TOWER_TYPES = new Set(['communication', 'radar', 'observation', 'radio', 'antenna']);
function infraKind(tags) {
    const tt = (tags['tower:type'] ?? '').toLowerCase();
    if (tags.man_made === 'mast' || tags.man_made === 'tower') {
        if (KOMM_TOWER_TYPES.has(tt) || tags['communication:mobile_phone'] === 'yes')
            return 'kommunikasjon';
        return 'mast';
    }
    if (tags.power === 'plant' || tags.power === 'substation')
        return 'kraft';
    if (tags.amenity === 'hospital')
        return 'sykehus';
    return null; // ikke infrastruktur vi bryr oss om her
}
/**
 * Map Overpass-elementer til `InfraSite[]`. Dropper elementer uten posisjon og
 * uten kjent infrastruktur-kategori (ingen «annet»-søppel inn i dekningsmodellen).
 */
export function osmToInfraSites(elements, source = 'OSM') {
    const out = [];
    for (const e of elements) {
        const lat = e.lat ?? e.center?.lat;
        const lon = e.lon ?? e.center?.lon;
        if (lat === undefined || lon === undefined)
            continue;
        const tags = e.tags ?? {};
        const kind = infraKind(tags);
        if (!kind)
            continue;
        const s = {
            id: `osm:${e.type}/${e.id}`,
            name: tags.name ?? tags.description ?? tags.operator ?? INFRA_FALLBACK_NAME[kind],
            lat, lon, kind, source,
        };
        const h = osmHeightM(tags);
        const ele = osmMeters(tags.ele);
        if (h !== undefined)
            s.heightM = h;
        if (ele !== undefined)
            s.elevationM = ele;
        if (tags['tower:type'])
            s.towerType = tags['tower:type'];
        if (tags.operator)
            s.operator = tags.operator;
        out.push(s);
    }
    return out;
}
const INFRA_FALLBACK_NAME = {
    kommunikasjon: 'Kommunikasjonsmast',
    mast: 'Mast/tårn',
    kraft: 'Kraftanlegg',
    sykehus: 'Sykehus',
};
/** Norsk etikett per infrastruktur-kategori (tegnforklaring). */
export const INFRA_KIND_LABEL = {
    kommunikasjon: 'Samband (mast/tårn)',
    mast: 'Andre master/tårn',
    kraft: 'Kraft (verk/trafo)',
    sykehus: 'Sykehus',
};
/** Alle infrastruktur-kategorier i fast rekkefølge (UI-iterasjon). */
export const INFRA_KINDS = ['kommunikasjon', 'mast', 'kraft', 'sykehus'];
/** Bbox-areal i kvadratgrader — brukes til å nekte for store Overpass-spørringer. */
export function bboxAreaDeg2(b) {
    return Math.abs(b.east - b.west) * Math.abs(b.north - b.south);
}
/** Rund bbox ut til et grovt rutenett (cache-nøkkel + gjenbruk mellom panoreringer). */
export function coarsenBBox(b, stepDeg = 0.25) {
    const f = (v, dir) => (dir < 0 ? Math.floor(v / stepDeg) : Math.ceil(v / stepDeg)) * stepDeg;
    return {
        west: round4(f(b.west, -1)), south: round4(f(b.south, -1)),
        east: round4(f(b.east, 1)), north: round4(f(b.north, 1)),
    };
}
function round4(n) { return Math.round(n * 10000) / 10000; }
//# sourceMappingURL=osm.js.map